<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import ContactAPI from 'dashboard/api/contacts';

const route = useRoute();
const loading = ref(true);
const error = ref('');
const data = ref(null);

const contact = computed(() => data.value?.contact || {});
const conversations = computed(() => data.value?.conversations || []);

// 定制: 顶部欢迎语 — 取第一个有 greeting 的会话(通常同收件箱), 页面顶部展示一次。
// 与公开H5一致, 让尘锋看到的对话记录也带欢迎语。
const pageGreetings = computed(() => {
  for (const conv of conversations.value) {
    if (conv.greetings?.length) {
      return { sender: conv.inbox_name || '客服', items: conv.greetings };
    }
  }
  return null;
});

const stripPhone = p => (p || '').replace(/^\+86[-\s]?/, '') || '-';

// 结构化消息类型 → 中文标签(非 text/input_text 类的消息降级展示)
const typeLabel = m => {
  const t = m.content_type;
  const map = {
    input_email: '邮箱',
    input_select: '选择',
    cards: '卡片',
    form: '表单',
    article: '文章',
    sticker: '表情',
    voice_call: '通话',
  };
  return map[t] || '';
};

const isOutgoing = m => m.message_type === 1;
const fmtTime = ts => {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleString('zh-CN', {
      hour12: false,
      timeZone: 'Asia/Shanghai',
    });
  } catch {
    return ts;
  }
};

const attachmentLabel = a =>
  a.filename || ({ image: '图片', audio: '语音', video: '视频', file: '文件' }[a.file_type] || '附件');

// 会话状态 → 中文标签 + 配色
const statusInfo = s => {
  const map = {
    open: { t: '开放', c: 'bg-green-50 text-green-700 ring-green-200' },
    resolved: { t: '已解决', c: 'bg-slate-100 text-slate-600 ring-slate-200' },
    pending: { t: '待处理', c: 'bg-amber-50 text-amber-700 ring-amber-200' },
    bot: { t: '机器人', c: 'bg-blue-50 text-blue-700 ring-blue-200' },
  };
  return map[s] || { t: s || '-', c: 'bg-slate-100 text-slate-600 ring-slate-200' };
};

const fetchRecord = async () => {
  loading.value = true;
  error.value = '';
  try {
    const res = await ContactAPI.chatRecord(route.params.contactId);
    data.value = res.data;
  } catch (e) {
    error.value = e?.response?.data?.error || e?.message || '加载失败';
  } finally {
    loading.value = false;
  }
};

onMounted(fetchRecord);
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-y-auto bg-n-surface-2">
    <div class="mx-auto w-full max-w-3xl flex flex-col gap-4 p-4 sm:p-6">
      <!-- 顶部联系人卡片 -->
      <div
        class="flex flex-col gap-2 rounded-2xl border border-n-weak bg-n-surface p-4 shadow-sm"
      >
        <h1 class="text-lg font-semibold text-n-slate-12">
          {{ contact.name || '未命名访客' }}
        </h1>
        <div class="flex flex-wrap items-center gap-2 text-sm">
          <span
            v-if="contact.phone_number"
            class="inline-flex items-center gap-1.5 rounded-full bg-n-surface-1 px-2.5 py-1 text-n-slate-11"
          >
            <span class="i-lucide-phone size-3.5 text-n-slate-9"></span>
            {{ stripPhone(contact.phone_number) }}
          </span>
          <span
            v-if="contact.email"
            class="inline-flex items-center gap-1.5 rounded-full bg-n-surface-1 px-2.5 py-1 text-n-slate-11"
          >
            <span class="i-lucide-mail size-3.5 text-n-slate-9"></span>
            {{ contact.email }}
          </span>
        </div>
      </div>

      <!-- 顶部欢迎语(客服气泡样式, 独立卡片; 不影响下方会话/状态/来源等信息) -->
      <div
        v-if="pageGreetings"
        class="flex flex-col gap-3 rounded-2xl border border-n-weak bg-n-surface p-4 shadow-sm"
      >
        <div class="text-xs font-medium text-n-slate-9">欢迎语</div>
        <div class="flex flex-col gap-2">
          <div
            v-for="(g, i) in pageGreetings.items"
            :key="i"
            class="flex flex-col gap-1 items-end"
          >
            <div class="px-1 text-xs text-n-slate-9">
              <span class="font-medium">{{ pageGreetings.sender }}</span>
            </div>
            <div
              class="max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap break-words shadow-sm bg-n-brand text-white rounded-br-md"
            >
              {{ g }}
            </div>
          </div>
        </div>
      </div>

      <!-- 加载/错误 -->
      <div v-if="loading" class="py-20 text-center text-sm text-n-slate-10">
        加载中...
      </div>
      <div v-else-if="error" class="py-20 text-center text-sm text-n-amber-12">
        {{ error }}
      </div>

      <!-- 无会话 -->
      <div
        v-else-if="!conversations.length"
        class="flex flex-col items-center py-20 text-sm text-n-slate-10"
      >
        <span class="i-lucide-message-square size-10 mb-3 opacity-60"></span>
        暂无对话记录
      </div>

      <!-- 会话流 -->
      <template v-else>
        <section
          v-for="conv in conversations"
          :key="conv.id"
          class="flex flex-col gap-3 rounded-2xl border border-n-weak bg-n-surface p-4 shadow-sm"
        >
          <header class="flex items-center justify-between">
            <div class="flex flex-col gap-0.5">
              <span class="text-sm font-semibold text-n-slate-12">
                {{ conv.inbox_name || '会话' }}
              </span>
              <span class="text-xs text-n-slate-9">{{ fmtTime(conv.created_at) }}</span>
            </div>
            <span
              class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset"
              :class="statusInfo(conv.status).c"
              >{{ statusInfo(conv.status).t }}</span
            >
          </header>

          <!-- 来源网址 醒目meta条 -->
          <div
            class="flex items-center gap-2 rounded-xl bg-n-surface-1 p-3"
          >
            <span class="i-lucide-link size-4 shrink-0 text-n-brand"></span>
            <span class="shrink-0 text-xs font-medium text-n-slate-9">来源网址</span>
            <a
              v-if="conv.referer"
              :href="conv.referer"
              target="_blank"
              rel="noopener noreferrer"
              class="text-sm font-medium text-n-brand hover:underline break-all"
              :title="conv.referer"
              >{{ conv.referer }}</a
            >
            <span v-else class="text-sm text-n-slate-9">-</span>
          </div>

          <div
            v-if="!conv.messages.length"
            class="rounded-xl bg-n-surface-1 py-6 text-center text-xs text-n-slate-9"
          >
            （无消息）
          </div>

          <!-- 气泡对话 -->
          <ul v-else class="flex flex-col gap-3 py-1">
            <li
              v-for="m in conv.messages"
              :key="m.id"
              class="flex flex-col gap-1"
              :class="isOutgoing(m) ? 'items-end' : 'items-start'"
            >
              <div class="flex items-center gap-1.5 px-1 text-xs text-n-slate-9">
                <span class="font-medium">{{
                  m.sender_name || (isOutgoing(m) ? '客服' : '访客')
                }}</span>
                <span class="text-n-slate-8">·</span>
                <span>{{ fmtTime(m.created_at) }}</span>
              </div>
              <div
                class="max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap break-words shadow-sm"
                :class="
                  isOutgoing(m)
                    ? 'bg-n-brand text-white rounded-br-md'
                    : 'bg-n-surface-1 text-n-slate-12 ring-1 ring-inset ring-n-weak rounded-bl-md'
                "
              >
                <span v-if="typeLabel(m)" class="mr-1 opacity-70"
                  >[{{ typeLabel(m) }}]</span
                >{{ m.content || '—' }}
              </div>
              <!-- 附件 -->
              <div v-if="m.attachments?.length" class="flex flex-col gap-1">
                <a
                  v-for="a in m.attachments"
                  :key="a.file_url"
                  :href="a.file_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex items-center gap-1 rounded-lg bg-n-surface-1 px-2.5 py-1.5 text-xs font-medium text-n-brand ring-1 ring-inset ring-n-weak hover:bg-n-surface"
                >
                  <span class="i-lucide-paperclip size-3.5"></span>
                  {{ attachmentLabel(a) }}
                </a>
              </div>
            </li>
          </ul>
        </section>
      </template>
    </div>
  </div>
</template>
